import {
  SHBox,
  SHHtmlBlock,
  SHPopper,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { TooltipAllowSVG } from "@components/svgs";
import { WarningSVG } from "@components/svgs/warning";
import { IconButton, useTheme } from "@mui/material";
import { hexToRGBA } from "@utils";
import { useEffect, useRef, useState } from "react";
import { CloseButton } from "../close";

export interface WarningButtonProps {
  margin?: string | number;
  title?: string | React.ReactNode;
  content?: string | React.ReactNode;
  secondaryTitle?: string | React.ReactNode;
  secondaryContent?: string | React.ReactNode;
  customSVG?: React.ReactNode;
  maxWidth?: string;
  [key: string]: any;
}
export const WarningButton = ({
  margin,
  title,
  content,
  secondaryTitle,
  secondaryContent,
  customSVG,
  maxWidth = "280px",
  ...others
}: WarningButtonProps) => {
  const { palette, shadows } = useTheme();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: any) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef, buttonRef, open]);

  return (
    <SHStack onClick={(e) => e.stopPropagation()}>
      <IconButton
        {...others}
        ref={buttonRef}
        onClick={handleClick}
        disableRipple
        sx={{
          width: "24px",
          height: "22px",
          borderRadius: "0",
          p: 0,
          "&:hover ,&:active": {
            opacity: 0.8,
          },
        }}
      >
        {customSVG ? (
          customSVG
        ) : (
          <WarningSVG
            backgroundColor={palette.warning.main}
            iconColor={palette.common.white}
          />
        )}
      </IconButton>

      <SHPopper
        anchorEl={anchorEl}
        open={open}
        placement="top-start"
        popperOptions={{ modifiers: [{ name: "flip", enabled: false }] }}
        sx={{ zIndex: 9999 }}
      >
        <SHBox pb={1}>
          <SHStack
            ref={wrapperRef}
            gap={
              (title || content) && (secondaryTitle || secondaryContent) ? 1 : 0
            }
            sx={{
              p: 2,
              pr: 4,
              maxWidth: maxWidth,
              minHeight: "20px",
              bgcolor: hexToRGBA(palette.common.white, 0.9),
              border: "1px solid #E3E3E3",
              backdropFilter: "blur(5px)",
              borderRadius: "3px",
              boxShadow: shadows[1],
              position: "relative",
            }}
          >
            <SHStack>
              {title && typeof title === "string" ? (
                <SHTypography variant="body1" zIndex="1">
                  {title}
                </SHTypography>
              ) : (
                title
              )}
              {content && typeof content === "string" ? (
                <SHHtmlBlock
                  variant="body3"
                  zIndex={1}
                  color={palette.text.third}
                  content={content}
                />
              ) : (
                <SHBox
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: palette.text.third,
                  }}
                >
                  {content}
                </SHBox>
              )}
            </SHStack>
            <SHStack>
              {secondaryTitle && typeof secondaryTitle === "string" ? (
                <SHTypography variant="body1" zIndex="1">
                  {secondaryTitle}
                </SHTypography>
              ) : (
                secondaryTitle
              )}
              {secondaryContent && typeof secondaryContent === "string" ? (
                <SHHtmlBlock
                  variant="body3"
                  zIndex={1}
                  color={palette.text.third}
                  content={secondaryContent}
                />
              ) : (
                <SHBox
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: palette.text.third,
                  }}
                >
                  {secondaryContent}
                </SHBox>
              )}
            </SHStack>
            <SHBox sx={{ position: "absolute", top: "10px", right: "10px" }}>
              <CloseButton onClick={handleClick} />
            </SHBox>
            <SHBox sx={{ position: "absolute", bottom: "-11.7px", left: 0 }}>
              <TooltipAllowSVG />
            </SHBox>
          </SHStack>
        </SHBox>
      </SHPopper>
    </SHStack>
  );
};
